import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { KioskShell } from "../components/KioskShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { SignaturePad } from "../components/SignaturePad";
import { toast } from "sonner";
import { Camera, Upload, ArrowRight, ArrowLeft, CheckCircle2, FileText, Sparkles, Loader2 } from "lucide-react";

const STEPS = ["Purchase Type", "Personal Info", "ID Capture", "Financing", "Trade-In", "Bill of Sale", "Finance Contract", "Done"];

const PURCHASE_TYPES = [
  {
    key: "with_reg",
    title: "Purchase with Registration",
    icon: "📝",
    desc: "Dealer handles all DMV paperwork on your behalf.",
    fees: ["7% Georgia TAVT", "Dealer Registration $199", "Title $18", "Plate $20", "Mailing $5"],
    accent: "border-[#D4AF37]",
    activeBg: "bg-[#D4AF37]/10",
  },
  {
    key: "without_reg",
    title: "Purchase without Registration",
    icon: "💵",
    desc: "Sales price only. You handle title, tag, registration independently.",
    fees: ["Vehicle sale price", "All other charges — N/A"],
    accent: "border-emerald-400",
    activeBg: "bg-emerald-500/10",
  },
  {
    key: "financed",
    title: "Financing",
    icon: "🏦",
    desc: "Roll the full out-the-door price into monthly payments.",
    fees: ["7% TAVT", "Dealer Registration $199", "Title $18 / Plate $20", "Mailing $5", "Monthly payment plan"],
    accent: "border-[#E63946]",
    activeBg: "bg-[#E63946]/10",
  },
];

export default function CheckoutWizard() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState({
    full_name: "",
    dob: "",
    address: "",
    phone: "",
    email: "",
    drivers_license: "",
    license_state: "",
    license_expiry: "",
    ssn_last4: "",
    insurance_provider: "",
    insurance_policy: "",
    employer: "",
    monthly_income: 0,
    housing_status: "rent",
    time_at_residence: "",
    employment_duration: "",
  });
  const [customerId, setCustomerId] = useState(null);
  const [purchaseType, setPurchaseType] = useState("");  // with_reg | without_reg | financed
  const [ocrLoading, setOcrLoading] = useState(false);
  const [idFront, setIdFront] = useState("");
  const [idBack, setIdBack] = useState("");
  const [insuranceImg, setInsuranceImg] = useState("");
  const [financing, setFinancing] = useState({
    requested_amount: 0,
    monthly_income: 0,
    housing_expense: 0,
    employer: "",
    employment_years: 0,
    bankruptcy_history: false,
  });
  const [trade, setTrade] = useState({
    has_trade: false,
    vin: "",
    make: "",
    model: "",
    year: new Date().getFullYear() - 5,
    mileage: 50000,
    condition: "good",
    accident_history: false,
    loan_payoff: 0,
  });
  const [deal, setDeal] = useState(null);
  const [billSig, setBillSig] = useState("");
  const [contractSig, setContractSig] = useState("");
  const billPadRef = useRef(null);
  const contractPadRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/vehicles/${vehicleId}`).then((r) => {
      setVehicle(r.data);
      setFinancing((f) => ({ ...f, requested_amount: r.data.price }));
    });
  }, [vehicleId]);

  const next = () => setStep((s) => {
    let n = s + 1;
    // Skip Financing step (4) and Finance Contract step (7) for non-financed purchases
    if (purchaseType && purchaseType !== "financed") {
      if (n === 4) n = 5;
      if (n === 7) n = 8;
    }
    return Math.min(n, STEPS.length);
  });
  const back = () => setStep((s) => {
    let n = s - 1;
    if (purchaseType && purchaseType !== "financed") {
      if (n === 4) n = 3;
      if (n === 7) n = 6;
    }
    return Math.max(n, 1);
  });

  const handleFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onIdFrontUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await handleFileToBase64(file);
    setIdFront(dataUrl);
    setOcrLoading(true);
    try {
      const base64 = dataUrl.split(",")[1];
      const { data } = await api.post("/ocr/extract", { image_base64: base64, doc_type: "drivers_license" });
      const f = data.fields || {};
      setCustomer((c) => ({
        ...c,
        full_name: f.full_name || c.full_name,
        address: f.address || c.address,
        dob: f.dob || c.dob,
        drivers_license: f.license_number || c.drivers_license,
        license_state: f.issue_state || c.license_state,
        license_expiry: f.expiry_date || c.license_expiry,
      }));
      toast.success("ID scanned · fields auto-filled");
    } catch (err) {
      toast.error("OCR failed — please enter fields manually");
    } finally {
      setOcrLoading(false);
    }
  };

  const onAnyUpload = (setter) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setter(await handleFileToBase64(file));
  };

  const saveCustomerAndContinue = async () => {
    if (!customer.full_name || !customer.email) {
      toast.error("Please enter at least name and email");
      return;
    }
    setSubmitting(true);
    try {
      let cid = customerId;
      if (cid) {
        await api.put(`/customers/${cid}`, customer);
      } else {
        const { data } = await api.post("/customers", customer);
        cid = data.id;
        setCustomerId(cid);
      }
      // Persist scanned DL / insurance images now so they're saved for EVERY flow
      // (not just financed). Silent-fail so kiosk flow never blocks on doc upload.
      const uploadDoc = async (imgB64, docType, filename) => {
        if (!imgB64) return;
        try {
          await api.post("/documents", {
            customer_id: cid,
            deal_id: null,
            doc_type: docType,
            filename,
            content_base64: imgB64,
            mime_type: "image/png",
          });
        } catch { /* silent */ }
      };
      await uploadDoc(idFront, "license_front", "license_front.png");
      await uploadDoc(idBack, "license_back", "license_back.png");
      await uploadDoc(insuranceImg, "insurance", "insurance_card.png");
      // Cache for reservation flow if user comes back to vehicle page later
      try {
        if (customer.email) localStorage.setItem("wakzy_kiosk_email", customer.email);
        if (customer.full_name) localStorage.setItem("wakzy_kiosk_name", customer.full_name);
      } catch { /* ignore storage errors */ }
      next();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const submitFinancing = async () => {
    if (!customerId) return;
    setSubmitting(true);
    try {
      await api.post("/finance-apps", {
        customer_id: customerId,
        vehicle_id: vehicleId,
        ...financing,
      });
      toast.success("Finance application submitted");
      next();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const submitTrade = async () => {
    setSubmitting(true);
    try {
      if (trade.has_trade && customerId) {
        const { has_trade, ...payload } = trade;
        await api.post("/trade-ins", { customer_id: customerId, ...payload });
      }
      // Create deal — fees driven by purchase_type
      const isWithoutReg = purchaseType === "without_reg";
      const isFinanced = purchaseType === "financed";
      const dealerFee = isWithoutReg ? 0 : 199;
      const tavt = isWithoutReg ? 0 : Math.round((vehicle.price + dealerFee) * 0.07 * 100) / 100;
      const { data } = await api.post("/deals", {
        vehicle_id: vehicleId,
        customer_id: customerId,
        sales_price: vehicle.price,
        dealer_fee: dealerFee,
        tavt,
        registration_fee: 0,
        tag_plate_fee: isWithoutReg ? 0 : 20,       // Plate
        title_application_fee: isWithoutReg ? 0 : 18, // Title
        mailing_fee: isWithoutReg ? 0 : 5,
        warranty_fee: 0,
        addons: 0,
        trade_in_credit: trade.has_trade ? Math.max(0, 5000) : 0,
        down_payment: 0,
        apr: isFinanced ? 8.99 : 0,
        term_months: isFinanced ? 60 : 0,
        payment_method: isFinanced ? "financed" : (isWithoutReg ? "cash" : "card"),
        purchase_type: purchaseType,
        sold_as_is: true,
        odometer_reading: vehicle.mileage || 0,
      });
      setDeal(data);
      next();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const signBillOfSale = async () => {
    const sig = billPadRef.current?.getDataURL();
    if (!sig) { toast.error("Please sign before continuing"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/deals/${deal.id}/sign`, {
        deal_id: deal.id,
        role: "customer",
        signature_data: sig,
        document_type: "bill_of_sale",
      });
      setDeal(data);
      setBillSig(sig);
      next();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const signContract = async () => {
    const sig = contractPadRef.current?.getDataURL();
    if (!sig) { toast.error("Please sign before continuing"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/deals/${deal.id}/sign`, {
        deal_id: deal.id,
        role: "customer",
        signature_data: sig,
        document_type: "finance_contract",
      });
      setDeal(data);
      setContractSig(sig);
      // NOTE: DL/insurance images were already uploaded in saveCustomerAndContinue
      // (customer-scoped) so we don't re-upload here — that would create duplicate
      // document rows for financed flows.
      next();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!vehicle) return null;

  return (
    <KioskShell step={step} totalSteps={STEPS.length} onBack={step > 1 ? back : () => navigate(`/kiosk/vehicle/${vehicleId}`)}>
      <section className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>
              {STEPS[step - 1]}
            </div>
            <h1 className="font-heading text-3xl mt-1">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h1>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Sale price</div>
            <div className="font-heading text-2xl" style={{ color: "var(--wakzy-gold)" }}>
              ${vehicle.price.toLocaleString()}
            </div>
          </div>
        </div>

        {/* STEP 1 — Purchase Type */}
        {step === 1 && (
          <div className="surface p-8 rounded-md fade-up">
            <div className="brand-divider mb-4 max-w-xs">
              <span className="dot" />
              <span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Choose your purchase path</span>
            </div>
            <h2 className="font-heading text-3xl mb-2 chrome-text inline-block">How would you like to buy?</h2>
            <p className="text-slate-400 mb-7">Pick one. You can change this later — it controls which fees and steps apply.</p>
            <div className="grid lg:grid-cols-3 gap-4">
              {PURCHASE_TYPES.map((p) => {
                const active = purchaseType === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setPurchaseType(p.key)}
                    className={`text-left p-6 rounded-md transition border-2 ${active ? p.accent + " " + p.activeBg : "border-white/10 hover:border-white/30"}`}
                    data-testid={`purchase-${p.key}`}
                  >
                    <div className="text-4xl mb-3">{p.icon}</div>
                    <div className="font-heading text-xl text-white mb-2">{p.title}</div>
                    <div className="text-sm text-slate-400 mb-4">{p.desc}</div>
                    <ul className="space-y-1">
                      {p.fees.map((f, i) => (
                        <li key={i} className="text-xs text-slate-300 flex items-start gap-2">
                          <span className="text-[#D4AF37] mt-0.5">•</span> {f}
                        </li>
                      ))}
                    </ul>
                    {active && (
                      <div className="mt-4 text-xs label-eyebrow flex items-center gap-1" style={{ color: "var(--wakzy-gold)" }}>
                        <CheckCircle2 className="w-3 h-3" /> Selected
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end mt-8">
              <Button
                onClick={next}
                disabled={!purchaseType}
                className="btn-cta rounded-sm h-12 px-8"
                data-testid="purchase-next-btn"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 — Personal Info */}
        {step === 2 && (
          <div className="surface p-8 rounded-md fade-up">
            <p className="text-slate-400 mb-6">Tell us about yourself. You can scan your ID in the next step to auto-fill missing fields.</p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { k: "full_name", label: "Full legal name", required: true },
                { k: "email", label: "Email", type: "email", required: true },
                { k: "phone", label: "Phone" },
                { k: "dob", label: "Date of birth", type: "date", placeholder: "MM/DD/YYYY" },
                { k: "address", label: "Address", full: true },
                { k: "drivers_license", label: "Driver license #" },
                { k: "license_state", label: "License state" },
                { k: "license_expiry", label: "License expiry", type: "date", placeholder: "MM/DD/YYYY" },
                { k: "ssn_last4", label: "SSN (last 4)" },
                { k: "insurance_provider", label: "Insurance provider" },
                { k: "insurance_policy", label: "Insurance policy #" },
                { k: "employer", label: "Employer" },
                { k: "monthly_income", label: "Monthly income ($)", type: "number" },
              ].map((f) => (
                <div key={f.k} className={f.full ? "md:col-span-2" : ""}>
                  <Label className="text-sm text-slate-300">
                    {f.label}{f.required && <span className="text-[#E63946]"> *</span>}
                  </Label>
                  <Input
                    type={f.type === "date" ? "text" : (f.type || "text")}
                    inputMode={f.type === "date" ? "numeric" : undefined}
                    placeholder={f.placeholder || ""}
                    value={customer[f.k] || ""}
                    onChange={(e) =>
                      setCustomer({
                        ...customer,
                        [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value,
                      })
                    }
                    className="bg-[#030712] border-white/10 mt-1 h-11"
                    data-testid={`customer-${f.k}-input`}
                  />
                  {f.type === "date" && (
                    <p className="text-[10px] text-slate-500 mt-1">Type date as MM/DD/YYYY</p>
                  )}
                </div>
              ))}
              <div>
                <Label className="text-sm text-slate-300">Housing status</Label>
                <Select value={customer.housing_status} onValueChange={(v) => setCustomer({ ...customer, housing_status: v })}>
                  <SelectTrigger className="bg-[#030712] border-white/10 mt-1 h-11" data-testid="customer-housing-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="own">Own</SelectItem>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-slate-300">Time at residence</Label>
                <Input value={customer.time_at_residence} onChange={(e) => setCustomer({ ...customer, time_at_residence: e.target.value })} className="bg-[#030712] border-white/10 mt-1 h-11" placeholder="e.g. 3 years" />
              </div>
              <div>
                <Label className="text-sm text-slate-300">Employment duration</Label>
                <Input value={customer.employment_duration} onChange={(e) => setCustomer({ ...customer, employment_duration: e.target.value })} className="bg-[#030712] border-white/10 mt-1 h-11" placeholder="e.g. 5 years" />
              </div>
            </div>
            <div className="flex justify-end mt-8">
              <Button onClick={saveCustomerAndContinue} disabled={submitting} className="btn-cta rounded-sm h-12 px-8" data-testid="step-personal-next-btn">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 — ID Capture */}
        {step === 3 && (
          <div className="surface p-8 rounded-md fade-up">
            <div className="flex items-center gap-2 mb-6 text-sm text-[#D4AF37]">
              <Sparkles className="w-4 h-4" /> Powered by AI OCR — fields will auto-fill from your license.
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              <DocUpload label="Driver's License — FRONT" value={idFront} onUpload={onIdFrontUpload} loading={ocrLoading} testid="id-front" />
              <DocUpload label="Driver's License — BACK" value={idBack} onUpload={onAnyUpload(setIdBack)} testid="id-back" />
              <DocUpload label="Insurance Card" value={insuranceImg} onUpload={onAnyUpload(setInsuranceImg)} testid="insurance-card" />
            </div>
            <div className="flex justify-between mt-8">
              <Button onClick={back} variant="outline" className="rounded-sm h-12 px-6 bg-transparent">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={next} className="btn-cta rounded-sm h-12 px-8" data-testid="step-id-next-btn">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 — Financing */}
        {step === 4 && (
          <div className="surface p-8 rounded-md fade-up finance-bg" style={{ backgroundBlendMode: "overlay" }}>
            <p className="text-slate-300 mb-6">Quick credit application. Subject to lender review.</p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { k: "requested_amount", label: "Amount requested ($)", type: "number" },
                { k: "monthly_income", label: "Monthly income ($)", type: "number" },
                { k: "housing_expense", label: "Housing expense ($)", type: "number" },
                { k: "employer", label: "Employer" },
                { k: "employment_years", label: "Years at employer", type: "number" },
              ].map((f) => (
                <div key={f.k}>
                  <Label className="text-sm text-slate-300">{f.label}</Label>
                  <Input
                    type={f.type || "text"}
                    value={financing[f.k]}
                    onChange={(e) =>
                      setFinancing({
                        ...financing,
                        [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value,
                      })
                    }
                    className="bg-[#030712] border-white/10 mt-1 h-11"
                    data-testid={`financing-${f.k}-input`}
                  />
                </div>
              ))}
              <div className="flex items-center gap-3 mt-4 md:col-span-2 surface p-4 rounded-sm">
                <Switch checked={financing.bankruptcy_history} onCheckedChange={(v) => setFinancing({ ...financing, bankruptcy_history: v })} data-testid="bankruptcy-switch" />
                <span className="text-sm">I have a bankruptcy in the last 7 years</span>
              </div>
            </div>
            <div className="flex justify-between mt-8">
              <Button onClick={back} variant="outline" className="rounded-sm h-12 px-6 bg-transparent">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={submitFinancing} disabled={submitting} className="btn-cta rounded-sm h-12 px-8" data-testid="step-financing-next-btn">
                Submit &amp; Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4 — Trade-In */}
        {step === 5 && (
          <div className="surface p-8 rounded-md fade-up">
            <div className="flex items-center gap-3 mb-6">
              <Switch checked={trade.has_trade} onCheckedChange={(v) => setTrade({ ...trade, has_trade: v })} data-testid="trade-has-switch" />
              <span>I have a vehicle to trade in</span>
            </div>
            {trade.has_trade && (
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { k: "vin", label: "VIN" },
                  { k: "make", label: "Make" },
                  { k: "model", label: "Model" },
                  { k: "year", label: "Year", type: "number" },
                  { k: "mileage", label: "Mileage", type: "number" },
                  { k: "loan_payoff", label: "Outstanding loan payoff ($)", type: "number" },
                ].map((f) => (
                  <div key={f.k}>
                    <Label className="text-sm text-slate-300">{f.label}</Label>
                    <Input
                      type={f.type || "text"}
                      value={trade[f.k]}
                      onChange={(e) =>
                        setTrade({
                          ...trade,
                          [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value,
                        })
                      }
                      className="bg-[#030712] border-white/10 mt-1 h-11"
                      data-testid={`trade-${f.k}-input`}
                    />
                  </div>
                ))}
                <div>
                  <Label className="text-sm text-slate-300">Condition</Label>
                  <Select value={trade.condition} onValueChange={(v) => setTrade({ ...trade, condition: v })}>
                    <SelectTrigger className="bg-[#030712] border-white/10 mt-1 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3 surface p-4 rounded-sm md:col-span-2">
                  <Switch checked={trade.accident_history} onCheckedChange={(v) => setTrade({ ...trade, accident_history: v })} />
                  <span className="text-sm">Vehicle has accident history</span>
                </div>
              </div>
            )}
            <div className="flex justify-between mt-8">
              <Button onClick={back} variant="outline" className="rounded-sm h-12 px-6 bg-transparent">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={submitTrade} disabled={submitting} className="btn-cta rounded-sm h-12 px-8" data-testid="step-trade-next-btn">
                {trade.has_trade ? "Submit & Continue" : "Skip & Continue"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 5 — Bill of Sale */}
        {step === 6 && deal && (
          <div className="surface p-8 rounded-md fade-up">
            <h2 className="font-heading text-2xl mb-6">Review &amp; sign your Bill of Sale</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="label-eyebrow mb-3">Vehicle</div>
                <div className="surface p-4 rounded-sm mb-6">
                  <div className="font-heading text-lg">{vehicle.year} {vehicle.make} {vehicle.model}</div>
                  <div className="text-xs font-mono text-slate-400 mt-1">VIN {vehicle.vin}</div>
                </div>
                <div className="label-eyebrow mb-3">Buyer</div>
                <div className="surface p-4 rounded-sm text-sm">
                  <div>{customer.full_name}</div>
                  <div className="text-slate-400">{customer.address}</div>
                  <div className="text-slate-400">{customer.email} · {customer.phone}</div>
                </div>
              </div>
              <div>
                <div className="label-eyebrow mb-3">Financial summary</div>
                <div className="surface p-4 rounded-sm divide-y divide-white/5 text-sm">
                  {(() => {
                    if (purchaseType === "without_reg") {
                      return [["Vehicle Sale Price", deal.sales_price], ["Other charges", "N/A"]];
                    }
                    return [
                      ["Vehicle Sale Price", deal.sales_price],
                      ["Dealer Registration Fee", deal.dealer_fee],
                      ["Title Application Fee", deal.title_application_fee ?? 18],
                      ["Tag & Plate Fee", deal.tag_plate_fee ?? 20],
                      ["Mailing Fee", deal.mailing_fee ?? 5],
                      ["TAVT (7%)", deal.tavt ?? deal.taxes],
                      ["Trade-in credit", -deal.trade_in_credit],
                      ["Down payment", -deal.down_payment],
                    ];
                  })().map(([k, v]) => (
                    <div key={k} className="flex justify-between py-2">
                      <span className="text-slate-400">{k}</span>
                      <span>{typeof v === "number" ? `$${Math.abs(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : v}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-3 font-heading text-lg" style={{ color: "var(--wakzy-gold)" }}>
                    <span>{purchaseType === "financed" ? "Amount financed" : "Total due"}</span>
                    <span>${(deal.amount_financed ?? deal.total ?? 0).toLocaleString()}</span>
                  </div>
                  {purchaseType === "financed" && (
                    <div className="flex justify-between py-2 text-sm">
                      <span className="text-slate-400">~{deal.term_months} mo @ {deal.apr}% APR</span>
                      <span className="font-heading">${deal.monthly_payment}/mo</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-8">
              <div className="label-eyebrow mb-3">Sign here to accept the Bill of Sale</div>
              <SignaturePad ref={billPadRef} onChange={setBillSig} />
            </div>

            <div className="flex justify-between mt-8">
              <Button onClick={back} variant="outline" className="rounded-sm h-12 px-6 bg-transparent">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div className="flex gap-3">
                <a
                  href={`${API}/deals/${deal.id}/pdf/bill-of-sale${deal.access_token ? `?token=${deal.access_token}` : ""}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center px-6 h-12 rounded-sm border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 text-sm font-bold"
                  data-testid="download-bos-btn"
                >
                  <FileText className="w-4 h-4 mr-2" /> Preview PDF
                </a>
                <Button onClick={signBillOfSale} disabled={submitting} className="btn-cta rounded-sm h-12 px-8" data-testid="sign-bos-btn">
                  Sign &amp; Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 6 — Finance Contract */}
        {step === 7 && deal && (
          <div className="surface p-8 rounded-md fade-up">
            <h2 className="font-heading text-2xl mb-6">Retail Installment Contract</h2>
            <div className="surface p-6 rounded-sm">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                {[
                  ["APR", `${deal.apr}%`],
                  ["Term", `${deal.term_months} mo`],
                  ["Monthly Payment", `$${deal.monthly_payment}`],
                  ["Amount Financed", `$${deal.amount_financed.toLocaleString()}`],
                  ["Finance Charge", `$${(deal.monthly_payment * deal.term_months - deal.amount_financed).toFixed(2)}`],
                  ["Total of Payments", `$${(deal.monthly_payment * deal.term_months).toFixed(2)}`],
                ].map(([k, v]) => (
                  <div key={k} className="surface p-3 rounded-sm">
                    <div className="label-eyebrow mb-1">{k}</div>
                    <div className="font-heading text-lg">{v}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-6 leading-relaxed">
                Truth in Lending disclosures apply. By signing, you agree to repay the
                amount financed plus all finance charges in monthly installments per
                the terms above. Buyer may be entitled to a refund of part of the
                finance charge upon early payoff per applicable law.
              </p>
            </div>

            <div className="mt-8">
              <div className="label-eyebrow mb-3">Sign here to accept the Finance Contract</div>
              <SignaturePad ref={contractPadRef} onChange={setContractSig} />
            </div>

            <div className="flex justify-between mt-8">
              <Button onClick={back} variant="outline" className="rounded-sm h-12 px-6 bg-transparent">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={signContract} disabled={submitting} className="btn-cta rounded-sm h-12 px-8" data-testid="sign-contract-btn">
                Sign &amp; Finalize <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 7 — Done */}
        {step === 8 && deal && (
          <div className="surface p-12 rounded-md fade-up text-center">
            <CheckCircle2 className="w-16 h-16 mx-auto mb-6" style={{ color: "var(--wakzy-gold)" }} />
            <h2 className="font-heading text-4xl mb-3">You're all set, {customer.full_name?.split(" ")[0]}.</h2>
            <p className="text-slate-300 max-w-xl mx-auto mb-8">
              Your paperwork is signed and stored securely. A sales associate will
              meet you in the lounge with your keys. Download a copy of your documents
              below.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a
                href={`${API}/deals/${deal.id}/pdf/bill-of-sale${deal.access_token ? `?token=${deal.access_token}` : ""}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center px-6 h-12 rounded-sm btn-gold text-sm"
                data-testid="final-bos-btn"
              >
                <FileText className="w-4 h-4 mr-2" /> Bill of Sale PDF
              </a>
              {purchaseType === "financed" && (
                <a
                  href={`${API}/deals/${deal.id}/pdf/finance-contract${deal.access_token ? `?token=${deal.access_token}` : ""}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center px-6 h-12 rounded-sm btn-navy text-sm"
                  data-testid="final-contract-btn"
                >
                  <FileText className="w-4 h-4 mr-2" /> Finance Contract PDF
                </a>
              )}
              <Button onClick={() => navigate("/")} variant="outline" className="rounded-sm h-12 px-6 bg-transparent">
                Return home
              </Button>
            </div>
          </div>
        )}
      </section>
    </KioskShell>
  );
}

function DocUpload({ label, value, onUpload, loading, testid }) {
  return (
    <label className="block cursor-pointer">
      <div className="label-eyebrow mb-2">{label}</div>
      <div
        className="aspect-[3/2] rounded-md border-2 border-dashed border-[#D4AF37]/40 bg-[#D4AF37]/5 hover:bg-[#D4AF37]/10 flex flex-col items-center justify-center overflow-hidden relative"
        data-testid={`upload-box-${testid}`}
      >
        {value ? (
          <img src={value} alt="" className="w-full h-full object-cover" />
        ) : (
          <>
            <Camera className="w-8 h-8 mb-2 text-[#D4AF37]" />
            <span className="text-sm text-slate-300">Tap to capture / upload</span>
          </>
        )}
        {loading && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-sm">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Scanning…
          </div>
        )}
      </div>
      <input
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onUpload}
        className="hidden"
        data-testid={`upload-input-${testid}`}
      />
    </label>
  );
}
